import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { BADGES } from '../config/constants';
import { SoundEffectService } from './SoundEffectService';
import { ShareCardService } from './ShareCardService';
import i18n from '../i18n';

export { BADGES };

const STORAGE_KEY = 'achievements_unlocked';
const activeToasts = [];

function repositionToasts() {
  let offset = 24;
  for (const el of activeToasts) {
    el.style.bottom = `${offset}px`;
    offset += el.offsetHeight + 12;
  }
}

export const AchievementsService = {
  getUnlocked() {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); }
    catch { return new Set(); }
  },

  /** Returns badge def if newly unlocked, null otherwise. */
  async tryUnlock(id) {
    const unlocked = this.getUnlocked();
    if (unlocked.has(id)) return null;
    unlocked.add(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...unlocked]));

    const uid = auth?.currentUser?.uid;
    if (uid && db) {
      setDoc(doc(db, 'users', uid), { achievements: [...unlocked] }, { merge: true })
        .catch((e) => console.warn('AchievementsService: Firestore sync failed', e));
    }
    return BADGES[id] ?? null;
  },

  /**
   * Merges badges unlocked on other devices into local storage on login, so
   * a fresh device (or cleared localStorage) doesn't silently drop
   * previously-unlocked badges the next time tryUnlock() writes back to
   * Firestore with only this device's (smaller) local set.
   */
  hydrateFromRemote(remoteData) {
    const remoteBadges = Array.isArray(remoteData?.achievements) ? remoteData.achievements : [];
    if (!remoteBadges.length) return;
    const merged = new Set([...this.getUnlocked(), ...remoteBadges]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...merged]));
  },

  showBadgeToast(badge) {
    if (!badge) return;
    SoundEffectService.playCelebration();
    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    Object.assign(el.style, {
      position: 'fixed', left: '50%', transform: 'translateX(-50%) translateY(80px)',
      background: '#1E1B4B', color: '#fff', borderRadius: '16px',
      padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px',
      fontFamily: 'inherit', zIndex: '10000',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
      maxWidth: '90vw', whiteSpace: 'nowrap',
    });
    const emojiEl = document.createElement('span');
    emojiEl.style.fontSize = '26px';
    emojiEl.textContent = badge.emoji;

    const eyebrowEl = document.createElement('div');
    Object.assign(eyebrowEl.style, {
      fontSize: '10px', color: '#ADA8F7', fontWeight: '700',
      textTransform: 'uppercase', letterSpacing: '.05em',
    });
    eyebrowEl.textContent = i18n.t('achievements.badgeUnlocked');

    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, { fontSize: '15px', fontWeight: '800' });
    titleEl.textContent = i18n.t(badge.title);

    const textWrap = document.createElement('div');
    textWrap.append(eyebrowEl, titleEl);

    const shareBtn = document.createElement('button');
    shareBtn.type = 'button';
    shareBtn.textContent = '📤';
    shareBtn.setAttribute('aria-label', i18n.t('achievements.share'));
    Object.assign(shareBtn.style, {
      marginLeft: '4px', background: 'rgba(255,255,255,0.12)', border: 'none',
      borderRadius: '999px', width: '32px', height: '32px', fontSize: '14px',
      cursor: 'pointer', flexShrink: '0',
    });
    shareBtn.addEventListener('click', () => {
      const title = i18n.t(badge.title);
      ShareCardService.generateBadgeCard(badge, title).then((blob) => ShareCardService.shareOrDownload(
        blob,
        `sublingox-${badge.title.split('.').pop()}.png`,
        i18n.t('achievements.shareText', { badge: title }),
      ));
    });

    el.append(emojiEl, textWrap, shareBtn);
    document.body.appendChild(el);
    activeToasts.push(el);
    repositionToasts();
    requestAnimationFrame(() => { el.style.transform = 'translateX(-50%) translateY(0)'; });
    setTimeout(() => {
      el.style.transform = 'translateX(-50%) translateY(80px)';
      setTimeout(() => {
        el.remove();
        const idx = activeToasts.indexOf(el);
        if (idx !== -1) activeToasts.splice(idx, 1);
        repositionToasts();
      }, 350);
    }, 3200);
  },
};
